
import { createContext, useState } from 'react'
import { browserName, browserVersion } from 'react-device-detect'
import jwt_decode from 'jwt-decode'
import { sendMessageToApp } from 'services/connectToApp'

import { api } from 'utils/api'
import { getError } from 'utils/helpers/getError'
import { useCustomContext } from 'utils/helpers/useCustomContext'
import { GenericResponse, GenericStateType } from 'utils/types'

import {
  AuthChangePasswordType, AuthContextType, AuthCreatePasswordRequestType, AuthDecodedUserType, AuthExternalBody,
  AuthLoginApiResponseType,
  AuthLoginRequestType, AuthRecoveryPasswordFormType, AuthUserStateType,
} from './authTypes'


const INITIAL_AUTH_CONTEXT: AuthContextType = {
  setLoading: () => null,
  setError: () => null,
  clearError: () => null,
  dispatchLogout: () => null,
  logout: () => null,
  userLoad: () => new Promise(() => null),
  checkAuth: () => new Promise(() => null),
  login: () => null,
  externalLogin:() => null,
  recoveryPassword: () => new Promise(() => null),
  changePassword: () => new Promise(() => null),
  createNewPassword: () => new Promise(() => null),
  authState: {
    loading: false,
    error: '',
    state: { user: null },
  },
}

const AuthContext = createContext<AuthContextType>(INITIAL_AUTH_CONTEXT)

const AuthProvider: React.FC = ({ children }) => {
  const [authState, setAuthState] = useState<GenericStateType<AuthUserStateType>>(
    INITIAL_AUTH_CONTEXT.authState,
  )

  const setLoading = (value = true) =>
    setAuthState(prevState => ({ ...prevState, loading: value }))

  const setError = (msg: string) =>
    setAuthState(prevState => ({ ...prevState, loading: false, error: msg }))

  const clearError = () =>
    setAuthState(prevState => ({ ...prevState, error: '' }))

  const dispatchLogout = async () => {
    await localStorage.clear()
    setAuthState(prevState => ({ ...prevState, state: { user: null } }))
  }

  const logout = async () => {
    try {
      const refreshToken = await localStorage.getItem('REFRESHTOKEN')

      if (refreshToken) {
        const data = {
          refreshToken,
        }

        api.post<GenericResponse<string>>('Auth/Logout', data)
      }
      dispatchLogout()
      sendMessageToApp({ route:'index', json: { logout : true }  })
    } catch (err) {
      const message = getError(err)

      setError(message)
      dispatchLogout()
    }
  }

  const userLoad = async (token: string) => {
    const decodedUser: AuthDecodedUserType = jwt_decode(token)

    const user: AuthDecodedUserType = {
      ...decodedUser,
      consumerActive: decodedUser.active === 'true',
    }

    setAuthState(prevState => ({
      ...prevState,
      loading: false,
      state: { user },
    }))
  }

  const checkAuth = async () => {
    const token: string | null = await localStorage.getItem('ACCESSTOKEN')
    const refreshToken: string | null = await localStorage.getItem(
      'REFRESHTOKEN',
    )

    if (token && refreshToken) await userLoad(token)
  }

  const login = async (data: AuthLoginRequestType) => {
    try {
      setLoading()

      const sendData = {
        ...data,
        origin: `${browserName} ${browserVersion}`
      }

      const res = await api.post<GenericResponse<AuthLoginApiResponseType>>('Auth/LoginV2', sendData)

      await localStorage.setItem('ACCESSTOKEN', res.data.payload.accessToken)
      await localStorage.setItem('REFRESHTOKEN', res.data.payload.refreshToken)
      sendMessageToApp({ route:'(routes)', json: res.data.payload  })
      setLoading(false)
      userLoad(res.data.payload.accessToken)
    } catch (err) {
      const msg = getError(err)

      setError(msg)
    }
  }
  const externalLogin = async (data: AuthExternalBody) => {
    try {
      setLoading()
      await localStorage.setItem('ACCESSTOKEN', data.accessToken)
      await localStorage.setItem('REFRESHTOKEN', data.refreshToken)
      setLoading(false)

      if(data.paymentgateway)
        window.location.href = data.paymentgateway

    } catch (err) {
      const msg = getError(err)

      setError(msg)
    }
  }
  const recoveryPassword = async (data: AuthRecoveryPasswordFormType) => {
    try {
      setLoading()
      await api.post<GenericResponse<null>>('Auth/PasswordRecoveryToken', data)

      setLoading(false)

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const changePassword = async (data: AuthChangePasswordType) => {
    try {
      setLoading()
      await api.post('User/UpdatePassword', data)
      setLoading(false)

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const createNewPassword = async (data: AuthCreatePasswordRequestType, token: string) => {
    try {
      setLoading()
      await api.post('Auth/PasswordRecoveryChange', data, { headers: { Authorization: `Token ${token}` } })
      setLoading(false)

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  return (
    <AuthContext.Provider
      value={{
        authState,
        setLoading,
        setError,
        clearError,
        checkAuth,
        login,
        externalLogin,
        logout,
        recoveryPassword,
        changePassword,
        createNewPassword,
      }}>
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => useCustomContext<AuthContextType>(AuthContext)

export { AuthProvider, useAuth }
